import { useEffect, useId, useRef } from 'react';

const SIZE_CLASS = {
  sm:   'sm:max-w-sm',
  md:   'sm:max-w-lg',
  lg:   'sm:max-w-2xl',
  xl:   'sm:max-w-4xl',
  full: 'sm:max-w-none',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function Modal({ title, onClose, children, wide, size, ariaLabel }) {
  const finalSize = size || (wide ? 'lg' : 'md');
  const sizeClass = SIZE_CLASS[finalSize] || SIZE_CLASS.md;
  const titleId = useId();

  const dialogRef = useRef(null);
  const prevFocusRef = useRef(null);

  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);

    const focusTimer = setTimeout(() => {
      if (!dialogRef.current) return;
      const inputs = dialogRef.current.querySelectorAll(
        'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])'
      );
      const target = inputs[0] || dialogRef.current.querySelector('button:not([disabled])');
      try { target?.focus(); } catch (_) {}
    }, 30);

    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      try { prevFocusRef.current?.focus?.(); } catch (_) {}
    };
  }, [onClose]);

  // Tylko mousedown bezposrednio na tle moze rozpoczac zamykanie. Inaczej drag
  // selekcji tekstu wewnatrz modala, ktory konczy sie poza nim, zamykalby okno.
  const handleMouseDown = e => {
    if (e.target === e.currentTarget) e.currentTarget.dataset.closing = '1';
    else delete e.currentTarget.dataset.closing;
  };
  const handleMouseUp = e => {
    if (e.target === e.currentTarget && e.currentTarget.dataset.closing === '1') {
      delete e.currentTarget.dataset.closing;
      onClose();
    } else {
      delete e.currentTarget.dataset.closing;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        className={`flex flex-col w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[10px] ${sizeClass}`}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-modal)',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <h2 id={titleId} style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>{title}</h2>
          <button
            onClick={onClose}
            aria-label="Zamknij"
            className="w-7 h-7 flex items-center justify-center rounded-btn text-lg leading-none transition-colors"
            style={{ color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg4)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >&times;</button>
        </div>
        <div className="overflow-y-auto px-6 py-5 flex-1">{children}</div>
      </div>
    </div>
  );
}
